import { useEffect, useState } from "react";
import { FileText, Replace } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { P } from "@/components/studio/portalKit";
import { PromptDialog } from "@/components/desk/PromptDialog";

const StudioPicker = ({ value, onChange }) => {
  const [studios, setStudios] = useState([]);
  useEffect(() => { api.get(`${P}/central/studios`).then(({ data }) => setStudios(data)); }, []);
  return (
    <select data-testid="roll-studio-select" className="h-10 w-full max-w-sm rounded-md border border-input bg-white px-3 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a studio…</option>{studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
};

export const DeskRolls = () => {
  const [studio, setStudio] = useState("");
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const load = () => studio && api.get(`${P}/central/serials`, { params: { studio_id: studio } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [studio]); // eslint-disable-line react-hooks/exhaustive-deps
  const replace = async (v) => {
    try { const { data } = await api.post(`${P}/central/serials/${target.id}/replace`, v); toast.success(`Replacement serial ${data.serial} issued`); setTarget(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <StudioPicker value={studio} onChange={setStudio} />
      {studio && rows.length === 0 && <p className="text-sm text-slate-500">No rolls linked to this studio.</p>}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {rows.map((r) => (
          <Card key={r.id} data-testid={`roll-${r.serial}`}>
            <CardContent className="space-y-2 p-4">
              <p className="font-semibold text-brand-ink">{r.serial}</p>
              <p className="text-xs text-slate-500">{r.product_name} · {r.balance_sqft}/{r.total_capacity} sqft left · <span className={r.status === "replaced" ? "font-semibold text-red-600" : ""}>{r.status === "replaced" ? "Replaced" : r.status}</span></p>
              {r.replaced_by && <p className="text-xs text-red-600">Replaced by {r.replaced_by}</p>}
              {r.replacement_of && <p className="text-xs text-emerald-700">Replacement for {r.replacement_of}</p>}
              {r.status !== "replaced" && <Button size="sm" variant="outline" data-testid={`replace-${r.serial}`} onClick={() => setTarget(r)}><Replace className="mr-1 h-3.5 w-3.5" />Replace roll</Button>}
            </CardContent>
          </Card>
        ))}
      </div>
      {target && <PromptDialog open title={`Replace ${target.serial}`} confirm="Issue replacement" fields={[{ key: "reason", label: "Reason", required: true }]} onClose={() => setTarget(null)} onSubmit={replace} />}
    </div>
  );
};

export const DeskInspections = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`${P}/central/inspection-reports`).then(({ data }) => setRows(data)); }, []);
  return (
    <div className="space-y-3">
      {rows.length === 0 && <p className="text-sm text-slate-500">No inspection reports yet.</p>}
      {rows.map((r) => (
        <Card key={r.id} data-testid={`desk-insp-${r.id}`}>
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div><p className="font-semibold text-brand-ink">{r.studio_name} · {r.customer_name} · {r.car_details}</p>
              <p className="text-xs text-slate-500">{fmtDate(r.updated_at)} · {r.exterior_count + r.interior_count} marks</p></div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/inspections/${r.id}/report.pdf`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid={`desk-insp-pdf-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button>
            </a>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
